import {
  getGroupedLabelPrintFormats,
  type LabelPrintFormat,
  LABEL_CONTENT_HEIGHT_MM,
  LABEL_CONTENT_WIDTH_MM,
  resolveLabelPrintLayout,
} from "@/core/printing/label-print-formats";
import {
  LABEL_PRINT_ROTATIONS,
  type LabelPrintRotation,
  rotateLabelClockwise,
  rotateLabelCounterClockwise,
} from "@/core/printing/label-print-rotation";
import { ProductLabelPrintSheet } from "./ProductLabelPrintSheet";
import type { ProductLabelData } from "./ProductLabelPrintSheet.types";

interface LabelFormatControlProps {
  formatId: string;
  onChange: (formatId: string) => void;
  compact?: boolean;
}

export function LabelFormatControl({ formatId, onChange, compact }: LabelFormatControlProps) {
  const groups = getGroupedLabelPrintFormats();

  return (
    <div className={`ph-label-format-control${compact ? " ph-label-format-control--compact" : ""}`}>
      {!compact && (
        <span className="ph-label-format-control-label small fw-semibold">
          Format imprimante
        </span>
      )}
      <select
        className="form-select form-select-sm"
        value={formatId}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Format papier étiquette"
      >
        {groups.map((group) => (
          <optgroup key={group.id} label={group.label}>
            {group.formats.map((format) => (
              <option key={format.id} value={format.id}>
                {format.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

interface LabelRotationControlProps {
  rotation: LabelPrintRotation;
  onChange: (rotation: LabelPrintRotation) => void;
  compact?: boolean;
}

export function LabelRotationControl({ rotation, onChange, compact }: LabelRotationControlProps) {
  return (
    <div className={`ph-label-rotation-control${compact ? " ph-label-rotation-control--compact" : ""}`}>
      {!compact && (
        <span className="ph-label-rotation-control-label small fw-semibold">Rotation contenu</span>
      )}
      <div className="d-flex flex-wrap align-items-center gap-2">
        <div className="btn-group btn-group-sm" role="group" aria-label="Rotation étiquette">
          <button
            type="button"
            className="btn btn-outline-secondary"
            title="Tourner à gauche"
            onClick={() => onChange(rotateLabelCounterClockwise(rotation))}
          >
            ↺
          </button>
          {LABEL_PRINT_ROTATIONS.map((deg) => (
            <button
              key={deg}
              type="button"
              className={`btn ${rotation === deg ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => onChange(deg)}
            >
              {deg}°
            </button>
          ))}
          <button
            type="button"
            className="btn btn-outline-secondary"
            title="Tourner à droite"
            onClick={() => onChange(rotateLabelClockwise(rotation))}
          >
            ↻
          </button>
        </div>
      </div>
    </div>
  );
}

interface LabelPrintPreviewProps {
  label: ProductLabelData;
  format: LabelPrintFormat;
  rotation: LabelPrintRotation;
}

/** Aperçu : format driver + rotation du contenu. */
export function LabelPrintPreview({ label, format, rotation }: LabelPrintPreviewProps) {
  const { pageWidthMm, pageHeightMm, contentWidthMm, contentHeightMm, contentScale, sheetTransform } =
    resolveLabelPrintLayout(format, rotation);

  return (
    <div className="ph-product-label-preview">
      <div
        className="ph-label-preview-stage"
        style={{ width: `${pageWidthMm}mm`, minHeight: `${pageHeightMm}mm` }}
      >
        <div
          className="ph-label-preview-sheet-wrap"
          style={{
            width: `${contentWidthMm}mm`,
            height: `${contentHeightMm}mm`,
            transform: sheetTransform,
          }}
        >
          <div
            style={{
              width: `${LABEL_CONTENT_WIDTH_MM}mm`,
              height: `${LABEL_CONTENT_HEIGHT_MM}mm`,
              transform: `scale(${contentScale})`,
              transformOrigin: "top left",
            }}
          >
            <ProductLabelPrintSheet label={label} />
          </div>
        </div>
      </div>
    </div>
  );
}
